import { useLocation, useNavigate } from 'react-router';
import { Drawer } from '@/components/Drawer';
import { useFormConfigStore } from '../state/store';
import { IdentitySection } from './sections/IdentitySection';
import { ValidationSection } from './sections/ValidationSection';
import { ValidationReadout } from './sections/ValidationReadout';
import { DisplaySection } from './sections/DisplaySection';
import { BehaviorSection } from './sections/BehaviorSection';
import { ConditionalVisibilitySection } from './sections/ConditionalVisibilitySection';
import { TypeSpecificSection } from './sections/TypeSpecificSection';

/**
 * Field editor drawer.
 *
 * Slide-out replacement for the legacy 2-pane FieldBasicsEditor host.
 * Mounts only when a field is selected (`selectedFieldName` set in
 * the form-config store). Dismiss via X / Escape / overlay click —
 * same chrome as RuleEditorDrawer (both use the shared `Drawer`
 * primitive).
 *
 * WU-3 (2026-05-09) replaced the temporary `FieldBasicsEditor` host
 * with three composable sections — Identity / Display / Behavior.
 * WU-4 added OCR + Conditional Visibility. WU-5 added the type-specific
 * dispatcher (TypeSpecificSection → DropdownOptionsSection /
 * LookupConfigSection).
 *
 * P3a (2026-07) retired the per-field Stages behavior editor and its
 * tab bar. Per-stage behavior authoring now lives on the job-level
 * Automations tab (which reuses the same shared behavior bodies), so
 * the drawer is once again a single-panel host for the field's
 * identity/validation/display/behavior sections.
 *
 * Each section subscribes to the store independently — the drawer
 * itself is a thin chrome-and-composition layer with no field-edit
 * logic.
 */

export interface FieldEditorDrawerProps {
  onClose: () => void;
}

export function FieldEditorDrawer({ onClose }: FieldEditorDrawerProps) {
  const content = useFormConfigStore((s) => s.content);
  const contentV3 = useFormConfigStore((s) => s.contentV3);
  const activeFormId = useFormConfigStore((s) => s.activeFormId);
  const selectedFieldName = useFormConfigStore((s) => s.selectedFieldName);
  const setPendingAutomationDraft = useFormConfigStore((s) => s.setPendingAutomationDraft);
  const jobTypeId = useFormConfigStore((s) => s.version?.jobTypeId);
  const field = content?.fields.find((f) => f.name === selectedFieldName) ?? null;
  const navigate = useNavigate();
  const location = useLocation();

  // P3c — the Rules tab was retired; the Automations tab (Job Config surface)
  // is now the sole rule/automation authoring surface. Both field-editor
  // affordances cross-navigate there. The Automations tab renders the same
  // read-only "Field checks · set on the Fields tab" derived display the Rules
  // tab used to show.
  function goToAutomations() {
    navigate(`/admin/job-types/${jobTypeId}?tab=automations`);
    onClose();
  }

  // Jump from the field's derived-validation readout to the Automations tab.
  function jumpToAutomations() {
    goToAutomations();
  }

  // Author a check the field settings can't derive. Instead of pre-creating a
  // rule (the legacy RulesTab path), stash the target form + field + effect in
  // the store; the Automations tab consumes `pendingAutomationDraft` on mount
  // and opens its create drawer pre-seeded to this form + field (it creates on
  // Save). The field editor is scoped to the active form, so `activeFormId` is
  // the field's owning form; fall back to searching contentV3 for the form that
  // actually contains the field (multi-form correctness — T10).
  function ownerFormId(): string {
    if (activeFormId) return activeFormId;
    const owner = contentV3?.forms.find((f) => f.fields.some((fl) => fl.name === selectedFieldName));
    return owner?.id ?? '';
  }
  // E-035 — the bridge stranded the operator in Automations after Finish/
  // Cancel. Capture where we're jumping FROM (this fields screen) so the
  // stepper can bounce back here instead of leaving the operator on the
  // Automations tab. Only this hand-off sets `returnTo` — a normal
  // "+ New automation" entry into the stepper has none, so its Cancel/Finish
  // behavior is unchanged.
  function addCustomCheck() {
    if (!selectedFieldName) return;
    const returnTo = `${location.pathname}${location.search}`;
    setPendingAutomationDraft({
      formId: ownerFormId(),
      fieldName: selectedFieldName,
      effectKey: 'validate_check',
      returnTo,
    });
    goToAutomations();
  }

  if (!field) return null;

  const title = `Edit field — ${field.name}`;

  // Sections commit on blur / change, so Save/Cancel would be a
  // misleading affordance — there's nothing to save (changes already
  // persisted to the draft). The drawer chrome's X / Esc / overlay
  // dismissals all just close the drawer; the draft retains every edit.
  return (
    <Drawer isOpen onClose={onClose} title={title} width="md">
      <div id="field-editor-basics-panel">
        <IdentitySection />
        <ValidationSection />
        <ValidationReadout onJumpToRules={jumpToAutomations} onAddCustomCheck={addCustomCheck} />
        <DisplaySection />
        <BehaviorSection />
        {/*
         * R-123 (2026-05-23) — the per-field OCR section was removed. OCR
         * field config (ocrExtracted / ocrAliases) now lives exclusively on
         * the OCR Profiles tab, materialised into FieldDefinition on
         * publish. The legacy `ocrFieldMapping` column is left on the
         * registry for backward compatibility but is no longer surfaced
         * here — having two visible OCR controls on the same field would
         * confuse operators and risk saving against the now-superseded
         * 133-type catalog model.
         */}
        <ConditionalVisibilitySection />
        <TypeSpecificSection />
      </div>
    </Drawer>
  );
}
